import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { commonImports } from '../shared/table-interface';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { NgxChartsModule, ScaleType } from '@swimlane/ngx-charts';
import { HttpClient } from '@angular/common/http';
import { Bundle, Observation } from 'fhir/r4';
import { CommonModule } from '@angular/common';
import { StateService } from '../shared/state.service';
import { backendEndPointToken } from '../app.config';
import { Subscription } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-admission-analytics',
  standalone: true,
  imports: [
    ...commonImports,
    MatSelectModule,
    NgxChartsModule,
    CommonModule,
    MatChipsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './admission-analytics.component.html',
  styleUrl: './admission-analytics.component.scss'
})
export class AdmissionAnalyticsComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private stateService = inject(StateService);
  private backendUrl = inject(backendEndPointToken);

  chartFilter = new FormControl('');
  chartFilterOptions: Array<{ code: string, display: string }> = [];

  categories: Array<{ code: string, display: string }> = [];
  selectedCategory = '';

  allObservations: Observation[] = [];
  loading = false;
  errorMessage = '';

  multi: any[] = [];
  view: [number, number] = [700, 300];
  legend = true;
  showLabels = true;
  animations = true;
  xAxis = true;
  yAxis = true;
  showYAxisLabel = true;
  showXAxisLabel = true;
  xAxisLabel = 'Date';
  yAxisLabel = 'Value';
  timeline = true;

  colorScheme = {
    name: 'whatever',
    domain: ['#327991', '#4980a0', '#639bb6', '#87b7c1'],
    selectable: true,
    group: ScaleType.Linear
  };

  ngxChartData?: any[];
  legendTitle = '';

  private encounterSubscription?: Subscription;
  private filterSubscription?: Subscription;

  formatDateStringToMediumDateAndTime(dateISOString: string) {
    const date = new Date(dateISOString);
    if (isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
      date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric' });
  }

  ngOnInit() {
    this.encounterSubscription = this.stateService.currentEncounter.subscribe(encounter => {
      if (encounter && encounter.id) {
        this.fetchObservations(encounter.id);
      } else {
        this.clearData();
      }
    });

    this.filterSubscription = this.chartFilter.valueChanges.subscribe(code => {
      this.updateChart(code);
    });
  }

  ngOnDestroy() {
    this.encounterSubscription?.unsubscribe();
    this.filterSubscription?.unsubscribe();
  }

  private fetchObservations(encounterId: string) {
    this.loading = true;
    this.errorMessage = '';
    const url = `${this.backendUrl}/Observation?encounter=Encounter/${encounterId}&status=final&_count=1000`;
    
    this.http.get<Bundle>(url).subscribe({
      next: (bundle) => {
        const entries = bundle.entry || [];
        this.allObservations = entries
          .map(e => e.resource as Observation)
          .filter(obs => obs && obs.valueQuantity && obs.valueQuantity.value !== undefined);
        
        this.processObservations();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching observations for analytics:', err);
        this.errorMessage = 'Failed to load observation data from the server.';
        this.loading = false;
      }
    });
  }

  private processObservations() {
    const categoryMap = new Map<string, string>();
    this.allObservations.forEach(obs => {
      const cat = this.getCategory(obs);
      categoryMap.set(cat.code, cat.display);
    });

    this.categories = Array.from(categoryMap.entries()).map(([code, display]) => ({ code, display }));

    if (this.categories.length > 0) {
      this.selectCategory(this.categories[0].code);
    } else {
      this.clearData();
    }
  }

  selectCategory(categoryCode: string) {
    this.selectedCategory = categoryCode;
    
    const obsInCategory = this.allObservations.filter(obs => this.getCategory(obs).code === categoryCode);
    
    const codeMap = new Map<string, string>();
    obsInCategory.forEach(obs => {
      const codeInfo = this.getObservationCode(obs);
      codeMap.set(codeInfo.code, codeInfo.display);
    });

    this.chartFilterOptions = Array.from(codeMap.entries()).map(([code, display]) => ({ code, display }));

    if (this.chartFilterOptions.length > 0) {
      this.chartFilter.setValue(this.chartFilterOptions[0].code);
    } else {
      this.chartFilter.setValue('');
      this.ngxChartData = undefined;
    }
  }

  private updateChart(code: string | null) {
    if (!code) {
      this.ngxChartData = undefined;
      return;
    }

    const obsWithCode = this.allObservations.filter(obs => {
      const catInfo = this.getCategory(obs);
      const codeInfo = this.getObservationCode(obs);
      return catInfo.code === this.selectedCategory && codeInfo.code === code;
    });

    // Sort chronologically (oldest first)
    obsWithCode.sort((a, b) => {
      const dateA = new Date(a.effectiveDateTime || a.effectivePeriod?.start || a.issued || 0).getTime();
      const dateB = new Date(b.effectiveDateTime || b.effectivePeriod?.start || b.issued || 0).getTime();
      return dateA - dateB;
    });

    const seriesData = obsWithCode.map(obs => ({
      name: this.formatDateStringToMediumDateAndTime(obs.effectiveDateTime || obs.effectivePeriod?.start || obs.issued || 'Unknown'),
      value: obs.valueQuantity?.value || 0
    }));

    const selectedCodeDisplay = this.chartFilterOptions.find(o => o.code === code)?.display || 'Value';
    const unit = obsWithCode[0]?.valueQuantity?.unit || '';

    this.legendTitle = selectedCodeDisplay;
    this.yAxisLabel = unit ? `${selectedCodeDisplay} (${unit})` : selectedCodeDisplay;
    this.ngxChartData = [{
      name: selectedCodeDisplay,
      series: seriesData
    }];
  }

  private getCategory(obs: Observation): { code: string, display: string } {
    const coding = obs.category?.[0]?.coding?.[0];
    const display = coding?.display || obs.category?.[0]?.text;
    const code = coding?.code || display?.toLowerCase().replace(/\s+/g, '-');
    return { 
      code: code || 'uncategorized', 
      display: display || 'Uncategorized' 
    };
  }

  private getObservationCode(obs: Observation): { code: string, display: string } {
    const coding = obs.code?.coding?.[0];
    const display = coding?.display || obs.code?.text || 'Unknown';
    const code = coding?.code || display.toLowerCase().replace(/\s+/g, '-');
    return { 
      code: code || 'unknown', 
      display 
    };
  }

  private clearData() {
    this.allObservations = [];
    this.categories = [];
    this.selectedCategory = '';
    this.chartFilterOptions = [];
    this.chartFilter.setValue('');
    this.ngxChartData = undefined;
  }
}
